import { Component, OnInit, ViewChild } from '@angular/core';
import dxTreeList, { CellPreparedEvent, ContentReadyEvent, Row, RowDraggingEndEvent, RowDraggingReorderEvent, RowDraggingStartEvent, RowRemovingEvent, SelectionChangedEvent } from 'devextreme/ui/tree_list';
import { Subject, combineLatest, filter, firstValueFrom, last, map, of, race, take, takeLast, takeUntil } from 'rxjs';
import { GenericCrudContextComponent } from 'src/app/libs/components/generic-crud/generic-crud-context.component';
import {  ProductFamily, TId } from 'src/app/models';
import { MasterApiService } from 'src/app/services/master-api.service';
import { ProductsFamiliesAddComponent } from '../../../configuration/products-families/products-families-add/products-families-add.component';
import { TabRole } from 'src/app/libs/components/tabs-host/tabs.service';
import { ProductsFamiliesEditComponent } from '../../../configuration/products-families/products-families-edit/products-families-edit.component';
import { TranslateService } from '@ngx-translate/core';
import { ProductsStorageService } from '../product-storage.service';
import { DxTreeListComponent } from 'devextreme-angular';

interface ITreeItem {
  id: TId;
  name: string;
  parentFamilyId: TId;
  companyId: TId;
  order: number;
}

@Component({
    selector: 'app-products-operative-products-contextualizer',
    templateUrl: './products-operative-products-contextualizer.component.html',
    styleUrls: ['./products-operative-products-contextualizer.component.css'],
    standalone: false
})
export class ProductsOperativeProductsContextualizerComponent extends GenericCrudContextComponent implements OnInit {
  // Copy of the original data, used to display the name, filter and
  // mantain the order and hierarchy of the tree
  data: ITreeItem[];
  selectionChanged$ = new Subject<SelectionChangedEvent>();
  @ViewChild('tree', { static: true }) tree!: DxTreeListComponent;
  

  readonly onDragStart = (e: RowDraggingStartEvent) => {
    if (e.itemData.id === 'root') {  // Prevent dragging the root node
      e.cancel = true;
    }
    const children = this.findChildren(e.itemData.id);
    e.component.getVisibleRows().forEach(row => {
      if (children.map(c => c.id).includes(row.node.data.id)) {
        row.node.visible = false;
      }
    });
  };

  readonly onDragEnd = async (e: RowDraggingEndEvent) => {
    const source: ProductFamily = e.itemData as ProductFamily;
    const target: ProductFamily = (e.component.getVisibleRows()[e.toIndex].node.data);
  
    if (!target || !source) return;
    if (source.id === target.id) return;
    if (source.parentFamilyId === target.id) return;
    if (!e.dropInsideItem && source.parentFamilyId === target.parentFamilyId) return;
  
    const children = this.findChildren(source.id);
    if (children.map(c => c.id).includes(target.id)) return;
  
    let msg = '';
    if (this.tree.selectedRowKeys.length > 1) {
      const families = await firstValueFrom(this.productData.getProductFamilies());
      // Collect names for selected keys
      const namesArray = this.tree.selectedRowKeys
        .map(key => families.find(f => f.id === key)?.name)
        .filter(name => !!name); // filter out undefined/null
    
      const names = namesArray.join(', '); // join with comma, no trailing comma
    
      msg = this.tr.instant('warnings.products.familiesTree.multipleFamilyMoveWarn', {
        from: names,
        to: target.name,
      });
    } else {
      msg = this.tr.instant('warnings.products.familiesTree.familyMoveWarn', {
        from: source.name,
        to: target.name,
      });
    }
    
    if (!await this.alerts.confirm(msg)) return;
  
    const payload = {
      action: 'setParent',
      parentFamilyId: target.id === 'root' ? null : target.id,
    };
  
    // Handle single or multiple row selection
    const idsToMove = this.tree.selectedRowKeys.length > 1
      ? this.tree.selectedRowKeys
      : [source.id];
  
    for (const id of idsToMove) {
      // Prevent setting parent to self or to descendant
      if (id === target.id) continue;
      const childrenOfId = this.findChildren(id);
      if (childrenOfId.map(c => c.id).includes(target.id)) continue;
  
      await firstValueFrom(this.api.resources.product.families.patch(id, payload));
    }
  
    this.productData.refreshProductFamilies();
    await this.fetchData(this.api.auth.userValue!.lastCompanyId);
    e.component.selectRows(idsToMove, false);
  };
  

  readonly onReorder = async (e: RowDraggingReorderEvent): Promise<void> => {
    const source: ProductFamily = e.itemData as ProductFamily;
    const target: ProductFamily = (e.component.getVisibleRows()[e.toIndex].node.data);
    if (e.dropInsideItem || source.parentFamilyId !== target.parentFamilyId) return;  // drags to another parent.
    const payload = {
      action: 'reorder',
      order: target.order,
    };
    await firstValueFrom(this.api.resources.product.families.patch(source.id, payload));
    this.productData.refreshProductFamilies();
    this.fetchData(this.api.auth.userValue!.lastCompanyId);

  }

  constructor(private api: MasterApiService, private tr: TranslateService, private productData: ProductsStorageService) {
    super();
    this.data = [];
  }

  async ngOnInit(): Promise<void> {
    this.api.auth.user$.pipe(
      takeUntil(this.destroy$),
      map(u => u?.lastCompanyId),
      filter(c => c !== undefined),
    ).subscribe(async companyId => {
      await this.fetchData(companyId!);
    });
  }

  async treeListReady(e: ContentReadyEvent): Promise<void> {
    if (e.component.getSelectedRowKeys().length === 0) {
      e.component.selectRows(['root'], false);
    }
    e.component.getVisibleRows().forEach(row => {
      const deleteButtonsElement: HTMLTableCellElement = (row as any).cells[1].cellElement;
      const deleteButton: HTMLAnchorElement | null = deleteButtonsElement.querySelector('.dx-link-delete');
      if (deleteButton) {
        deleteButton.style.display = 'none';
      }
    });
  }

  async selectionChanged(e: SelectionChangedEvent): Promise<void> {
    const rowId = e.selectedRowsData[0].id;
    this.contextChange.emit({
      search: { familyId: rowId === 'root' ? undefined : rowId},
    });
    this.selectionChanged$.next(e);

    if (this.fullyLoadedData$) {
      combineLatest([this.fullyLoadedData$, of(true)]).pipe(
        takeUntil(this.destroy$),
        map(([data]) => data),
        take(2),  // The first one is actually fired by selectionChanged, the second one is the real new data.
      ).pipe(last()).subscribe(data => {
        const row: HTMLTableRowElement = e.component.getRowElement(e.component.getRowIndexByKey(e.selectedRowKeys[0]))![0] as HTMLTableRowElement;
        const deleteButton: HTMLAnchorElement | null = row.cells[1].querySelector('.dx-link-delete');
        if (deleteButton) {
          deleteButton.style.display = data?.length === 0 ? 'inline-block' : 'none';
        }
        this.fullyLoadedData$?.pipe(
          takeUntil(race([this.destroy$, this.selectionChanged$])))
        .subscribe(data => {
          const row: HTMLTableRowElement = e.component.getRowElement(e.component.getRowIndexByKey(e.selectedRowKeys[0]))![0] as HTMLTableRowElement;
          const deleteButton: HTMLAnchorElement | null = row.cells[1].querySelector('.dx-link-delete');
          if (deleteButton) {
            deleteButton.style.display = data?.length === 0 ? 'inline-block' : 'none';
          }
        });
      });
    }
  }

  async rowRemoving(e: RowRemovingEvent): Promise<void> {
    await firstValueFrom(this.api.resources.product.families.delete(e.key));
  }

  async cellPrepared(e: CellPreparedEvent): Promise<void> {
    const cell: HTMLElement = e.cellElement;
  
    // 1. Hide checkboxes and remove reserved space
    if (e.columnIndex === 0) {
      const checkbox = cell.querySelector('.dx-select-checkbox') as HTMLElement;
      if (checkbox) {
        checkbox.style.display = 'none';
      }
  
      const iconContainer = cell.querySelector('.dx-treelist-icon-container.dx-editor-inline-block') as HTMLElement;
      if (iconContainer) {
        iconContainer.style.paddingRight = '0'; // removes the reserved space
      }
    }
  
    // 2. Make root node bold and remove action buttons
    if (e.row?.level === 0) {
      cell.style.fontWeight = 'bold';
      cell.querySelector('.dx-icon-edit')?.remove();
      cell.querySelector('.dx-link-delete')?.remove();
    }
  }
  
  
  

  async rowAdd(e: {row: Row}): Promise<void> {
    const data = e.row.data;
    const familyId = data.id === 'root' ? undefined : data.id;
    const tab = this.tabs.createTab(ProductsFamiliesAddComponent, TabRole.CREATION, {
      inputPrms: {
        initialFormValues: { companyId: data.companyId, parentFamilyId: familyId }
      }
    });
    tab.component.instance.entityCreated.pipe(
      takeUntil(this.destroy$),
      take(1),
      map(newItem => Array.isArray(newItem) ? newItem[0] : newItem)
    ).subscribe(newItem => {
      this.data.push({
        id: newItem.id,
        name: newItem.name,
        parentFamilyId: newItem.parentFamilyId || 'root',
        companyId: newItem.companyId,
        order: newItem.order,
      });
      this.productData.refreshProductFamilies();
      tab.close();
    });
  }

  onRowClick(e: any) {
    const rowId = e.data.id;
    const currentSelection = this.tree.instance.getSelectedRowKeys();
  
    // Detect Ctrl (or Cmd on Mac)
    const isMultiSelect = e.event.ctrlKey || e.event.metaKey;
  
    let newSelection: any[];
  
    if (isMultiSelect) {
      const alreadySelected = currentSelection.includes(rowId);
      newSelection = alreadySelected
        ? currentSelection.filter(id => id !== rowId)
        : [...currentSelection, rowId];
    } else {
      newSelection = [rowId]; // single selection
    }
  
    this.tree.instance.selectRows(newSelection, false);
  }
  
  


  async rowEdit(e: {row: Row, component: dxTreeList}): Promise<void> {
    const entity = await firstValueFrom(this.api.resources.product.families.get(e.row.data.id));

    const tab = this.tabs.createTab(ProductsFamiliesEditComponent, TabRole.EDITION, {inputPrms: { entity,  extraInputParams: {type: "goods-families"}, genericInfoAlternativeText:'common.tabTitle.productFamily' }});
    tab.component.instance.destroy$.pipe(
      takeUntil(this.destroy$),
      take(1),
    ).subscribe(async () => {
      if (tab.component.instance.entity?.parentFamilyId === null) {
        tab.component.instance.entity!.parentFamilyId = 'root';
        await e.component.refresh(true);
      }
    });
    tab.component.instance.entityChanged.pipe(
      takeUntil(this.destroy$),
      take(1),
    ).subscribe(_newItem => {
      const found = this.data.find(f => f.id === _newItem.id);
      // Update data
      if (found) {
        found.name = _newItem.name;
        found.order = _newItem.order;
        found.parentFamilyId = _newItem.parentFamilyId || 'root';
        found.companyId = _newItem.companyId;
      }
      this.productData.refreshProductFamilies();
      tab.close();
    });
  }

  private findChildren = (parentId: TId, acc?: ITreeItem[]): ITreeItem[] => {
    if (acc === undefined) {
      acc = [];
    }
    const tmp = this.data.filter(family => family.parentFamilyId === parentId);
    acc.push(...tmp);
    tmp.forEach(child => this.findChildren(child.id, acc));
    return acc;
  };

  private async fetchData(companyId: TId): Promise<void> {
    this.data = await firstValueFrom(this.productData.getProductFamilies().pipe(
      map(rs => rs.map(fs => {
        // Mantain a copy of the original data
        const result = Object.assign({}, fs);
        result.parentFamilyId = result.parentFamilyId || 'root';
        return result;
      }))
    ));
    this.data.forEach(f => f.parentFamilyId = f.parentFamilyId || 'root');
    const root: ProductFamily = {
      id: 'root',
      name: 'families',
      companyId,
      parentFamilyId: null,
    } as unknown as ProductFamily;
    this.data.unshift(root);
  }


  private setDeleteFamilyButtonVisibility(familyId:TId, visible:boolean) {
    const family = this.data.find(_ => _.id === familyId);
    if (!family) return;

    const tree = document.querySelector('dx-tree-list');
    if (!tree) return;

    const rows = tree.querySelectorAll('tr');
    for (let i = 0; i <= rows.length - 1; i++) {
      const td = rows[i].querySelector('td');
      if (td?.children[1]?.innerHTML === family.name) {
        const deleteButton:HTMLElement|null = rows[i].querySelector('.dx-link-delete');
        if (deleteButton) {
          deleteButton.style.visibility = visible ? 'visible' : 'hidden';
        }
      }
    }
  }

}
